<script lang="ts" generics="ViewZoneProps extends Record<string, unknown> = Record<string, never>">
    import { type Component, mount, onDestroy, onMount, unmount, untrack } from 'svelte'
    import type monaco from 'monaco-editor'
    import type {
        AvailableLanguages,
        AvailableProgrammingLanguages
    } from '../../../lib/Project.svelte'
    import { isTextFileLanguage, type TextFileLanguage } from '#lib/textFileLanguages.ts'
    import type { MonacoType } from '#lib/monaco/Monaco.ts'
    import { Monaco } from '#lib/monaco/Monaco.ts'
    import { generateTheme } from '#lib/monaco/editorTheme.ts'
    import { EDITOR_FONT } from '#lib/monaco/editorFont.ts'
    import type { BuildArtifact, Diagnostic } from '#lib/languages/commonLanguageFeatures.svelte.ts'
    import {
        parseProjectSourceUri,
        projectSourceUri,
        type ProjectModelIdentity
    } from '#lib/languages/service/uri.ts'
    import { resolveEditorModel, type EditorModelStore, type EditorSource } from './editorSource'
    import { zeroBasedLineToMonaco } from '#lib/languages/service/monacoConversions.ts'
    import { setModelBuildArtifacts } from '#lib/monaco/assemblyInsights.ts'
    import { quickFixRange, setModelQuickFixes } from '#lib/monaco/quickFixes.ts'
    import { CurrentLineReveal } from '#lib/monaco/currentLineReveal.ts'
    import { keepHoverReachable } from '#lib/monaco/hoverReachability.ts'
    import type { EditorLineColoring } from '#lib/monaco/lineColoring.ts'
    import { selectedLines } from '#lib/monaco/selectedLines.ts'
    import { setSourceHelpLanguage } from '#lib/sourceLanguageHelp/context.ts'
    import type { EditorModels } from '#lib/workbench/editorModels.ts'
    import {
        listingLineNumbers,
        listingLineNumberWidth,
        setModelListing,
        type ListingModel
    } from '#lib/monaco/listingModels.ts'
    import { setModelPseudocode, type PseudocodeModel } from '#lib/monaco/pseudocodeModels.ts'
    import { SOURCE_MAP_COLOR_OPACITY, SOURCE_MAP_SECTION_BORDER } from '#lib/Config.ts'
    import {
        lineActionContextKey,
        lineActionDescriptor,
        navigationActions,
        type EditorLineAction,
        type EditorNavigation
    } from '#lib/monaco/editorLineActions.ts'
    import { followNamesAndHistory } from '#lib/monaco/editorNavigation.ts'
    import { noteViewStateListing, viewStateFor } from '#lib/monaco/listingViewState.ts'
    import {
        noteViewStatePseudocode,
        pseudocodeViewStateFor
    } from '#lib/monaco/pseudocodeViewState.ts'
    import { openProjectResource } from '#lib/languages/service/navigation.ts'

    interface Props {
        disabled?: boolean
        /**
         * The single-source contract: one File, bound two ways. Hosts that bind it (a lesson
         * playground, an exam answer) both supply the text and receive the user's edits through it.
         * A multi-File host passes `source` instead and this is never written.
         */
        code: string
        codeOverride?: string
        /**
         * The multi-File contract. The key and the text travel together so they cannot disagree:
         * passing them as separate props let an effect run with the new File's key and the previous
         * File's text, which created that File's model holding the other one's source.
         */
        source?: EditorSource
        /** Keeps each Project File on its own Monaco model and therefore its own text Undo stack. */
        modelKey?: string
        /** Gives a Project File a stable URI that providers can route back to its Project session. */
        modelIdentity?: ProjectModelIdentity
        /** Model identities still owned by the Project; omitted outside the Project File editor. */
        retainedModelKeys?: readonly string[]
        /** A Workbench session owns models shared by its editor groups. */
        sharedModels?: EditorModels
        highlightedLine?: number
        /**
         * A Run is in flight: `highlightedLine` is drawn but not scrolled to, the host reveals
         * where the program stops or waits for input instead.
         */
        running?: boolean
        /** Matching source/assembly section backgrounds. */
        lineColoring?: EditorLineColoring
        /** Palette indices emphasized by the active source/assembly connections; others dim. */
        activeLineColors?: ReadonlySet<number>
        hasError?: boolean
        language: AvailableLanguages | AvailableProgrammingLanguages | TextFileLanguage
        diagnostics?: Diagnostic[]
        breakpoints?: number[]
        /** Lines where a Breakpoint set on another File, through a Source map, stops. */
        mappedBreakpoints?: number[]
        /** Text can be read-only while the Debug session still accepts breakpoint changes. */
        breakpointsEditable?: boolean
        editor?: monaco.editor.IStandaloneCodeEditor
        viewZones?: {
            afterLineNumber: number
            content: Component<ViewZoneProps>
            props: ViewZoneProps
        }[]
        buildArtifacts?: BuildArtifact[]
        /**
         * The Listing the model holds, when it is one: its gutter prints each line's address
         * instead of its number, and the Listing providers read it.
         */
        listing?: ListingModel
        /** The Pseudocode the model holds, when it is one, which its navigation reads. */
        pseudocode?: PseudocodeModel
        /** Whether a 0-based line can take a Breakpoint; every line by default. */
        canBreakAt?: (line: number) => boolean
        /** 0-based lines a Debug session patched, marked beside their line numbers. */
        patchedLines?: readonly number[]
        /**
         * 0-based lines whose code the program or a Poke changed at run time, marked as patched
         * lines are, with what runs there now in the mark's tooltip.
         */
        changedLines?: readonly { line: number; runsNow: string }[]
        /** A note written after a line's text, such as running from inside its instruction. */
        lineNote?: { line: number; text: string }
        /**
         * Commands on the cursor's line, in the context menu and on their keys, offered on the
         * lines where they are available: "Patch instruction…" on a Listing.
         */
        lineActions?: readonly EditorLineAction[]
        /**
         * Back and forward through the pane's navigation history, for a Listing or a Pseudocode:
         * Go Back and Go Forward in the context menu and on Alt+← and Alt+→, and the mouse's back
         * and forward buttons.
         */
        navigation?: EditorNavigation
        /**
         * Where each model was left, by key, kept by a host that outlives this editor: a pane
         * that shows a File's bytes and then its Listing again mounts a new editor, which goes
         * back to where the last one left the Listing. This editor's own by default.
         */
        viewStates?: Map<string, monaco.editor.ICodeEditorViewState | null>
        /** The shown model's text after each edit the person makes in it. */
        onChange?: (value: string) => void
        /** A change to any Project File's model, including one the editor is not showing. */
        onFileChange?: (path: string, value: string) => void
        /** A press on a line's glyph margin, 1-based, where a Breakpoint can go. */
        onBreakpointPress?: (line: number) => void
        /** The cursor's zero-based line, whoever moved it: the person, the debugger, a model switch. */
        onCursorMove?: (line: number) => void
        /** Zero-based lines covered by the user's selections, a click's single line included. */
        onLinesSelect?: (lines: number[]) => void
        /**
         * Go to Symbol or Go to Line moved the cursor: the zero-based line it left, sent before
         * the cursor's move.
         */
        onJump?: (line: number) => void
    }

    let {
        disabled = false,
        code = $bindable(),
        codeOverride,
        source,
        modelKey = 'default',
        modelIdentity,
        retainedModelKeys,
        sharedModels,
        highlightedLine = -1,
        running = false,
        lineColoring,
        activeLineColors,
        hasError = false,
        language,
        diagnostics = [],
        breakpoints = [],
        mappedBreakpoints = [],
        breakpointsEditable = true,
        editor = $bindable(),
        viewZones = [],
        buildArtifacts = [],
        listing,
        pseudocode,
        canBreakAt,
        patchedLines = [],
        changedLines = [],
        lineNote,
        lineActions,
        navigation,
        viewStates,
        onChange,
        onFileChange,
        onBreakpointPress,
        onCursorMove,
        onLinesSelect,
        onJump
    }: Props = $props()
    /**
     * Which model to show and what it should hold, as one value. A multi-File host supplies both
     * together; a single-source host's text is its `code` prop, or the compiled-code override when
     * there is one. That test is truthiness, not `??`: every emulator but x86 reports `''` from
     * `_getCompiledCode` for a program with nothing to expand, so a successful Build would
     * otherwise replace the program with an empty model.
     */
    /** How much smaller than the code a Listing's gutter prints its addresses, in rem. */
    const ADDRESS_GUTTER_SHRINK_REM = 0.2
    const activeSource = $derived<EditorSource>(
        source ?? { key: modelKey, value: codeOverride || code, identity: modelIdentity }
    )
    /** Monaco's id for the language: C shares the C++ tokenizer. */
    const monacoLanguageId = $derived(language === 'c' ? 'cpp' : language.toLowerCase())
    /** Which of the two a C or C++ model holds, which its tokenizer cannot tell the source help. */
    const helpLanguage = $derived(language === 'c' || language === 'cpp' ? language : undefined)
    let mockEditor: HTMLDivElement | null = $state(null)
    let monacoInstance: MonacoType | null = $state.raw(null)
    /**
     * Which model is attached to the editor. Reactive, because the decoration, marker, view-zone
     * and highlight effects below all key off it: with a plain variable each of them read `''` on
     * its first run, returned before reading anything tracked, and so registered no dependencies at
     * all and never ran again — leaving Monaco without a decorations collection or a single marker.
     */
    let activeModelKey = $state('')
    /**
     * The same key, untracked, for saving the outgoing model's view state inside `selectModel`.
     * Reading `activeModelKey` there would make the effect that calls `selectModel` depend on a
     * value that same call writes, so it would re-run itself on every model switch.
     */
    let viewStateKey = ''
    let hoveredGliphen: number | null = $state(null)
    /** The 0-based line the cursor is on, which the line actions are offered for. */
    let cursorLine = $state(-1)
    /** And its 0-based column, for an action on the name under the cursor, as Rename… is. */
    let cursorColumn = $state(-1)
    /**
     * The Listing the model shown was placed with, untracked: a view state saved over it is moved
     * onto the next Listing of the same File, whose lines may sit elsewhere (`viewStateFor`).
     */
    let placedListing: ListingModel['listing'] | undefined
    /**
     * Likewise the Pseudocode the model shown was placed with: a function decompiled again (after
     * a rename or a comment) keeps the same places in view (`pseudocodeViewStateFor`).
     */
    let placedPseudocode: PseudocodeModel | undefined
    // eslint-disable-next-line svelte/prefer-svelte-reactivity -- Monaco handles; nothing renders from them.
    const lineActionKeys = new Map<string, monaco.editor.IContextKey<boolean>>()
    /** The line actions with Go Back and Go Forward first, for an editor with a history. */
    const editorActions = $derived([
        ...(navigation ? navigationActions(navigation) : []),
        ...(lineActions ?? [])
    ])
    /** The name under the mouse a double-click would follow, drawn as a link. */
    let followDecorations: monaco.editor.IEditorDecorationsCollection | undefined
    let followedRange: monaco.IRange | undefined
    let destroyed = false
    /**
     * The editor this component created. `editor` is bound out to the host, and a host that swaps
     * one Editor for another shares the binding between them, so teardown disposes this one: going
     * through the binding could dispose the successor instead.
     */
    let ownEditor: monaco.editor.IStandaloneCodeEditor | undefined
    let applyingExternalValue = false
    let overflowWidgets: HTMLDivElement | null = null
    //Plain Maps, not reactive ones: nothing renders from them, and the effect that reconciles the
    //models both reads and writes them, which with reactive maps made it re-run on its own writes.
    // eslint-disable-next-line svelte/prefer-svelte-reactivity -- see above; no tracked consumer.
    const models = new Map<string, monaco.editor.ITextModel>()
    const modelViewStates: Map<string, monaco.editor.ICodeEditorViewState | null> = untrack(
        () => viewStates ?? new Map()
    )
    const toDispose: (monaco.IDisposable | (() => void))[] = []
    let el: HTMLDivElement | null = $state(null)

    $effect(() => {
        if (editor) {
            Monaco.setCustomTheme(generateTheme())
        }
    })

    onMount(async () => {
        const loadedMonaco = await Monaco.get()
        if (destroyed) return
        monacoInstance = loadedMonaco
        const editorElement = el
        if (!editorElement) return console.log('Wrapper element not valid', editorElement)
        await Monaco.registerLanguage(language)
        if (destroyed) return
        const mounted = activeSource
        const initialModel = resolveModel(loadedMonaco, mounted)
        setSourceHelpLanguage(initialModel, helpLanguage)
        viewStateKey = mounted.key
        activeModelKey = mounted.key
        overflowWidgets = document.createElement('div')
        //Keep Monaco's widget styles/theme while escaping the editor's local stacking context.
        overflowWidgets.className = 'monaco-overflow-widgets monaco-editor'
        //the nearest theme, not the app's: an editor embedded in a lecture or a language's docs
        //sits in a subtree with its own colours, and the widgets have to agree with it
        const overflowWidgetsHost =
            editorElement.closest<HTMLElement>('.theme-scope, .theme-root') ?? document.body
        overflowWidgetsHost.appendChild(overflowWidgets)
        const mountedEditor = loadedMonaco.editor.create(editorElement, {
            model: initialModel,
            theme: 'custom-theme',
            //Monaco's default is 'editable', which means it hides every validation decoration while
            //the editor is read-only — and this editor is read-only in exactly the states where the
            //diagnostics still matter: a Debug session, a Build snapshot, an exam. The error pill
            //and the console list keep reporting them there, so the squiggles must agree.
            renderValidationDecorations: 'on',
            //Context menus need the theme variables and decoration styles from the document.
            useShadowDOM: false,
            fixedOverflowWidgets: true,
            overflowWidgetsDomNode: overflowWidgets,
            minimap: { enabled: false },
            scrollbar: {
                vertical: 'auto',
                horizontal: 'auto'
            },
            colorDecorators: false,
            glyphMargin: true,
            //Monaco sizes the line-number column to the model's digit count and only uses this as
            //the floor, so a short file gets a narrow gutter and one past 99 or 999 lines widens it.
            //Two keeps files up to 99 lines from shifting the code every time a digit is added.
            lineNumbersMinChars: 2,
            cursorBlinking: 'phase',
            //Fira Code everywhere, at the size and line height the static code blocks standing in
            //for an editor are drawn with, so neither the font nor the lines move when it loads
            ...EDITOR_FONT,
            smoothScrolling: true,
            cursorSmoothCaretAnimation: 'on'
        })
        ownEditor = mountedEditor
        editor = mountedEditor
        toDispose.push(keepHoverReachable(mountedEditor, overflowWidgets))
        const observer = new ResizeObserver(() => {
            if (!mockEditor) return
            const bounds = mockEditor.getBoundingClientRect()
            mountedEditor.layout({
                width: bounds.width,
                height: bounds.height
            })
        })
        if (mockEditor) {
            observer.observe(mockEditor)
        }

        followDecorations = mountedEditor.createDecorationsCollection()
        /** The 1-based line of the glyph margin under the mouse, if a Breakpoint can go there. */
        const breakableGlyphLine = (e: monaco.editor.IEditorMouseEvent): number | null =>
            breakpointsEditable &&
            e.target.type === loadedMonaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN &&
            (canBreakAt?.(e.target.position.lineNumber - 1) ?? true)
                ? e.target.position.lineNumber
                : null
        toDispose.push(
            mountedEditor.onDidChangeCursorPosition((event) => {
                const left = cursorLine
                cursorLine = event.position.lineNumber - 1
                cursorColumn = event.position.column - 1
                //at once, not in an effect: a right click moves the cursor and opens the context
                //menu in the same event, and the menu reads the keys as they are then
                updateLineActionKeys(cursorLine, cursorColumn)
                //Monaco's own jumps, Go to Symbol's and Go to Line's
                if (event.source === 'code.jump') onJump?.(left)
                onCursorMove?.(cursorLine)
            }),
            mountedEditor.onDidChangeCursorSelection((event) => {
                //Model switches and debugger navigation are not a new user source selection.
                if (applyingExternalValue || event.source === 'api') return
                onLinesSelect?.(selectedLines(mountedEditor.getSelections() ?? []))
            }),
            mountedEditor.onMouseDown((e) => {
                const line = breakableGlyphLine(e)
                if (line !== null) onBreakpointPress?.(line)
            }),
            mountedEditor.onMouseLeave(() => {
                hoveredGliphen = null
            }),
            mountedEditor.onMouseMove((e) => {
                hoveredGliphen = breakableGlyphLine(e)
            })
        )
        toDispose.push(() => observer.disconnect())

        //a double-click on a name in a Listing or a Pseudocode follows it, and Alt+←, Alt+→ and the
        //mouse's buttons move through the pane's history there (decisions 7 and 8)
        toDispose.push(
            followNamesAndHistory(loadedMonaco, mountedEditor, editorElement, {
                navigation: () => navigation,
                follow: (name) =>
                    void openProjectResource(
                        name.resource,
                        { lineNumber: name.line, column: 1 },
                        mountedEditor
                    ),
                showFollowable
            })
        )
        toDispose.push(
            mountedEditor.onDidChangeModelContent(() => {
                if (disabled || applyingExternalValue) return
                const value = mountedEditor.getValue()
                //A multi-File host owns its Files and receives edits through `fileChange`; writing
                //`code` there would make this component a second writer of a prop the host is also
                //deriving, and whichever wrote last in a tick would win.
                if (!source) code = value
                onChange?.(value)
            })
        )
        //after the listeners, so the host hears where the cursor went
        applyingExternalValue = true
        try {
            placeModel(mountedEditor, loadedMonaco, mounted)
        } finally {
            applyingExternalValue = false
        }
    })

    /**
     * Puts a model the editor has just taken where an editor of this host last left it, or, shown
     * for the first time, on the line its source opens at, near the top of the view.
     */
    function placeModel(
        currentEditor: monaco.editor.IStandaloneCodeEditor,
        currentMonaco: MonacoType,
        shown: EditorSource
    ) {
        const viewState = modelViewStates.get(shown.key)
        //a Listing made again since keeps its addresses in view, not its line numbers, and a
        //Pseudocode decompiled again its statements, comments and names
        const placing = untrack(() => listing?.listing)
        placedListing = placing
        const decompiled = untrack(() => pseudocode)
        placedPseudocode = decompiled
        if (viewState) {
            currentEditor.restoreViewState(
                pseudocodeViewStateFor(viewStateFor(viewState, placing), decompiled)
            )
            return
        }
        const opensAt = shown.opensAt
        if (opensAt === undefined || opensAt < 0) return
        const lineNumber = zeroBasedLineToMonaco(opensAt)
        currentEditor.setPosition({ lineNumber, column: 1 })
        currentEditor.revealLineNearTop(lineNumber, currentMonaco.editor.ScrollType.Immediate)
    }

    function setModelValue(model: monaco.editor.ITextModel, value: string) {
        if (model.getValue() === value) return
        applyingExternalValue = true
        try {
            //External changes are authoritative (for example, a running program writing a live
            //File), so they start a fresh text Undo history instead of becoming an editor edit.
            model.setValue(value)
        } finally {
            applyingExternalValue = false
        }
    }

    function createModel(
        currentMonaco: MonacoType,
        value: string,
        modelLanguage: string,
        identity: ProjectModelIdentity | undefined
    ): monaco.editor.ITextModel {
        const uri = identity ? projectSourceUri(currentMonaco, identity) : undefined
        const model = currentMonaco.editor.createModel(value, modelLanguage, uri)
        //Per model, not per editor: a rename or a code action returns edits for several resources
        //at once, and Monaco applies them to models the editor is not showing. Listening only on
        //the active model dropped those edits, and the next time that File was opened its model was
        //overwritten from the Project, losing them for good.
        const listener = model.onDidChangeContent(() => {
            if (disabled || applyingExternalValue) return
            const changed = parseProjectSourceUri(model.uri)
            if (changed?.sourceKind === 'live' && !changed.view) {
                onFileChange?.(changed.path, model.getValue())
            }
        })
        toDispose.push(() => listener.dispose())
        return model
    }

    function modelStore(currentMonaco: MonacoType): EditorModelStore<monaco.editor.ITextModel> {
        return {
            get: (key) => models.get(key),
            set: (key, model) => void models.set(key, model),
            delete: (key) => void models.delete(key),
            isDisposed: (model) => model.isDisposed(),
            create: (source) => {
                const model = createModel(
                    currentMonaco,
                    source.value,
                    monacoLanguageId,
                    source.identity
                )
                model.setEOL(0)
                return model
            },
            setValue: setModelValue
        }
    }

    /** The model for `next`, from the models the session shares or from this editor's own. */
    function resolveModel(currentMonaco: MonacoType, next: EditorSource) {
        return sharedModels
            ? sharedModels.resolve(currentMonaco, next, monacoLanguageId)
            : resolveEditorModel(modelStore(currentMonaco), next)
    }

    /** `next`, not `source`: shadowing the prop here is how the two could quietly diverge again. */
    function selectModel(next: EditorSource) {
        const currentEditor = editor
        const currentMonaco = monacoInstance
        //a host that swaps its content while Monaco is still loading can destroy this component
        //between the editor's creation and this effect; a disposed editor throws on setModel
        if (destroyed || !currentEditor || !currentMonaco) return
        //saved before the model is resolved: a Listing made again replaces the model under the
        //same key, disposing the one the editor holds, whose state would then be lost
        const leaving = currentEditor.getModel() ? currentEditor.saveViewState() : null
        noteViewStateListing(leaving, placedListing)
        noteViewStatePseudocode(leaving, placedPseudocode)
        //and the keyboard: Monaco drops it with the view of a model it lets go, which a Listing
        //made again after a rename typed there would take from the person
        const typing = currentEditor.hasTextFocus()
        const model = resolveModel(currentMonaco, next)
        setSourceHelpLanguage(model, helpLanguage)
        //the language mounted with is registered in onMount, but a group that opened on a C file
        //and then shows its Generated assembly reaches here with one nothing has registered yet.
        //Registration is memoized; a model made before it lands reads as plain text and takes the
        //language once Monaco learns its id
        void Monaco.registerLanguage(language).catch((error) =>
            console.error(`Language registration failed for ${language}`, error)
        )
        if (currentEditor.getModel() !== model) {
            //the link under the mouse is the outgoing model's
            showFollowable(undefined)
            if (viewStateKey && leaving) modelViewStates.set(viewStateKey, leaving)
            applyingExternalValue = true
            try {
                currentEditor.setModel(model)
                placeModel(currentEditor, currentMonaco, next)
                if (typing && !currentEditor.hasTextFocus()) currentEditor.focus()
            } finally {
                applyingExternalValue = false
            }
        }
        viewStateKey = next.key
        activeModelKey = next.key
        //the same model's Listing with a Debug session's patches keeps its line table
        placedListing = untrack(() => listing?.listing)
        placedPseudocode = untrack(() => pseudocode)
        cursorLine = (currentEditor.getPosition()?.lineNumber ?? 0) - 1
        cursorColumn = (currentEditor.getPosition()?.column ?? 0) - 1
    }

    $effect(() => {
        selectModel(activeSource)
    })

    /** `lineNote`, written after the end of its line's text. */
    function lineNoteDecorations(
        currentEditor: monaco.editor.IStandaloneCodeEditor,
        currentMonaco: MonacoType
    ): monaco.editor.IModelDeltaDecoration[] {
        const model = currentEditor.getModel()
        if (!lineNote || !model || lineNote.line >= model.getLineCount()) return []
        const line = lineNote.line + 1
        const end = model.getLineMaxColumn(line)
        return [
            {
                range: new currentMonaco.Range(line, end, line, end),
                options: {
                    after: { content: `  ⟵ ${lineNote.text}`, inlineClassName: 'line-note' }
                }
            }
        ]
    }

    function lineActionKey(action: EditorLineAction) {
        let key = lineActionKeys.get(action.id)
        if (!key && editor) {
            key = editor.createContextKey(lineActionContextKey(action), false)
            lineActionKeys.set(action.id, key)
        }
        return key
    }

    function updateLineActionKeys(line: number, column: number) {
        untrack(() => {
            for (const action of editorActions)
                lineActionKey(action)?.set(line >= 0 && action.available(line, Math.max(column, 0)))
        })
    }

    /** Draws a name as a link while the mouse is over it, or none. */
    function showFollowable(range: monaco.IRange | undefined) {
        if (
            range === followedRange ||
            (range && followedRange && monacoInstance?.Range.equalsRange(range, followedRange))
        )
            return
        followedRange = range
        followDecorations?.set(
            range ? [{ range, options: { inlineClassName: 'followable-name' } }] : []
        )
    }

    //each line action is a Monaco action while the host offers it, shown in the context menu and
    //bound to its keys only where its context key says it is available
    $effect(() => {
        const currentEditor = editor
        const currentMonaco = monacoInstance
        const actions = editorActions
        if (!currentEditor || !currentMonaco || !actions.length) return
        const registered = untrack(() =>
            actions.map((action) => {
                lineActionKey(action)
                return currentEditor.addAction(
                    lineActionDescriptor(action, currentMonaco, (target) => {
                        const position = target.getPosition()
                        const line = (position?.lineNumber ?? 0) - 1
                        const column = Math.max((position?.column ?? 1) - 1, 0)
                        if (line >= 0 && action.available(line, column)) action.run(line, column)
                    })
                )
            })
        )
        return () => {
            for (const action of registered) action.dispose()
            for (const action of actions) lineActionKeys.get(action.id)?.set(false)
        }
    })
    //and again whenever what decides it changes: a Run starting or ending, another model
    $effect(() => {
        const actions = editorActions
        void activeModelKey
        if (!editor || !actions.length) return
        for (const action of actions)
            lineActionKey(action)?.set(
                cursorLine >= 0 && action.available(cursorLine, Math.max(cursorColumn, 0))
            )
    })

    $effect(() => {
        if (!activeModelKey) return
        const model = sharedModels ? editor?.getModel() : models.get(activeSource.key)
        if (!model || model.isDisposed()) return
        if (sharedModels) {
            sharedModels.artifacts(activeSource.key, buildArtifacts)
            return
        }
        return setModelBuildArtifacts(model.uri.toString(), buildArtifacts)
    })

    //a Listing's model is read by the Listing providers, and its gutter prints addresses; declared
    //after the model is selected, so the two change together
    $effect(() => {
        if (!activeModelKey || !listing) return
        const model = editor?.getModel()
        if (!model || model.isDisposed()) return
        return setModelListing(model.uri.toString(), listing)
    })
    $effect(() => {
        if (!activeModelKey || !pseudocode) return
        const model = editor?.getModel()
        if (!model || model.isDisposed()) return
        return setModelPseudocode(model.uri.toString(), pseudocode)
    })
    /** Whether the gutter prints a Listing's addresses, so leaving one restores line numbers. */
    let addressGutter = false
    /** The same, for the editor's class, which prints the addresses smaller. */
    let addressGutterShown = $state(false)
    $effect(() => {
        if (!activeModelKey || !editor) return
        if (listing) {
            const { listing: shown, shortAddresses } = listing
            const currentEditor = editor
            const fit = () =>
                currentEditor.updateOptions({
                    lineNumbers: listingLineNumbers(shown, shortAddresses),
                    lineNumbersMinChars: addressGutterChars(
                        listingLineNumberWidth(shown, shortAddresses)
                    )
                })
            fit()
            addressGutter = true
            addressGutterShown = true
            //the gutter's width is counted in the editor font's digits, so a new font size refits it
            const fontOption = monacoInstance?.editor.EditorOption.fontInfo
            const listener = currentEditor.onDidChangeConfiguration((event) => {
                if (fontOption !== undefined && event.hasChanged(fontOption)) fit()
            })
            return () => listener.dispose()
        } else if (addressGutter) {
            editor.updateOptions({ lineNumbers: 'on', lineNumbersMinChars: 2 })
            addressGutter = false
            addressGutterShown = false
        }
    })

    /**
     * How many of the editor font's digits a Listing's address gutter needs for `chars` characters
     * printed 0.2rem smaller (see `.address-gutter`): 64-bit addresses are long, so the smaller
     * font gives the code more of the width.
     */
    function addressGutterChars(chars: number): number {
        const fontSize = monacoInstance
            ? editor?.getOption(monacoInstance.editor.EditorOption.fontSize)
            : undefined
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize)
        if (!fontSize || !(rem > 0)) return chars
        const scale = Math.max(0.5, (fontSize - ADDRESS_GUTTER_SHRINK_REM * rem) / fontSize)
        return Math.max(2, Math.ceil(chars * scale))
    }

    $effect(() => {
        if (!retainedModelKeys) return
        const retained = new Set(retainedModelKeys)
        const current = activeSource.key
        for (const key of modelViewStates.keys()) {
            if (key !== current && !retained.has(key)) modelViewStates.delete(key)
        }
        if (sharedModels) return
        for (const [key, model] of models) {
            if (key === current || retained.has(key)) continue
            model.dispose()
            models.delete(key)
            modelViewStates.delete(key)
        }
    })
    onDestroy(() => {
        destroyed = true
        //for the next editor of a host that keeps them, with the Listing it was over
        if (viewStates && viewStateKey && ownEditor?.getModel()) {
            const state = ownEditor.saveViewState()
            noteViewStateListing(state, placedListing)
            noteViewStatePseudocode(state, placedPseudocode)
            viewStates.set(viewStateKey, state)
        }

        toDispose.forEach((disposable) => {
            if (typeof disposable === 'function') return disposable()
            disposable?.dispose()
        })
        decorations?.clear()
        ownEditor?.dispose()
        if (editor === ownEditor) editor = undefined
        overflowWidgets?.remove()
        overflowWidgets = null
        if (!sharedModels)
            for (const model of models.values()) if (!model.isDisposed()) model.dispose()
        models.clear()
        if (!viewStates) modelViewStates.clear()
    })

    let decorations: monaco.editor.IEditorDecorationsCollection | undefined = $state.raw()
    let sectionLayoutRevision = $state(0)

    $effect(() => {
        const listener = editor?.onDidChangeHiddenAreas(() => sectionLayoutRevision++)
        return () => listener?.dispose()
    })

    /**
     * The program's current line, apart from every other decoration: it moves far more often than
     * anything else changes, and moving it must not rebuild the source-map sections and the
     * Breakpoints along with it.
     */
    let lineDecorations: monaco.editor.IEditorDecorationsCollection | undefined = $state.raw()
    /**
     * The current line when a Breakpoint is on it, whose glyph is then drawn to stand out of the
     * line's color, else -1: the line moving between lines without Breakpoints changes nothing.
     */
    const breakpointOnHighlightedLine = $derived(
        breakpoints.includes(highlightedLine) || mappedBreakpoints.includes(highlightedLine)
            ? highlightedLine
            : -1
    )

    $effect(() => {
        if (!activeModelKey) return
        const collection = editor?.createDecorationsCollection()
        const lineCollection = editor?.createDecorationsCollection()
        decorations = collection
        lineDecorations = lineCollection
        return () => {
            collection?.clear()
            lineCollection?.clear()
        }
    })

    $effect(() => {
        if (activeModelKey && editor && viewZones.length > 0) {
            const viewZoneEditor = editor
            const coloring = lineColoring
            let currentViewZones = [] as {
                id: string
                domNode: HTMLElement
                wrapper: HTMLElement
                marginDomNode?: HTMLElement
                observer: ResizeObserver
                component: Record<string, unknown>
            }[]
            currentViewZones = []
            viewZoneEditor.changeViewZones(function (changeAccessor) {
                viewZones.forEach((zone, index) => {
                    const domNode = document.createElement('div')
                    const line = zone.afterLineNumber - 1
                    const section = coloring?.ranges.find(
                        (range) => range.startLine <= line && range.endLine >= line
                    )
                    let marginDomNode: HTMLElement | undefined
                    if (section) {
                        //Expansion rows and their gutter belong to the original assembly section.
                        domNode.className = `compiled-section compiled-section-zone compiled-section-color-${section.colorIndex}`
                        marginDomNode = document.createElement('div')
                        marginDomNode.className = `compiled-section-margin compiled-section-color-${section.colorIndex}`
                        if (
                            SOURCE_MAP_SECTION_BORDER.width > 0 &&
                            line === section.endLine &&
                            !viewZones
                                .slice(index + 1)
                                .some(
                                    (candidate) =>
                                        candidate.afterLineNumber === zone.afterLineNumber
                                )
                        ) {
                            //The section ends after its final expansion row, not above it.
                            domNode.classList.add('compiled-section-border-bottom')
                            marginDomNode.classList.add('compiled-section-border-bottom')
                        }
                    }
                    const wrapper = document.createElement('div')
                    viewZoneEditor.applyFontInfo(wrapper)
                    const Component = zone.content
                    const props = zone.props
                    const component = mount(Component, {
                        target: wrapper,
                        props
                    })
                    domNode.appendChild(wrapper)

                    const id = changeAccessor.addZone({
                        afterLineNumber: zone.afterLineNumber,
                        get heightInPx() {
                            return wrapper.getBoundingClientRect().height
                        },
                        domNode,
                        marginDomNode
                    })
                    const observer = new ResizeObserver(() => {
                        const height = wrapper.getBoundingClientRect().height
                        if (!height) return
                        viewZoneEditor.changeViewZones((accessor) => {
                            accessor.layoutZone(id)
                        })
                    })
                    observer.observe(wrapper)
                    currentViewZones.push({
                        id,
                        domNode,
                        wrapper,
                        marginDomNode,
                        observer,
                        component
                    })
                })
            })
            const fontOption = monacoInstance?.editor.EditorOption.fontInfo
            const fontListener = viewZoneEditor.onDidChangeConfiguration((event) => {
                if (fontOption !== undefined && !event.hasChanged(fontOption)) return
                currentViewZones.forEach((zone) => viewZoneEditor.applyFontInfo(zone.wrapper))
            })
            return () => {
                fontListener.dispose()
                currentViewZones.forEach((zone) => {
                    zone.observer.disconnect()
                    void unmount(zone.component)
                    zone.domNode.remove()
                    zone.marginDomNode?.remove()
                })
                if (destroyed) return
                viewZoneEditor.changeViewZones((changeAccessor) => {
                    currentViewZones.forEach((zone) => {
                        changeAccessor.removeZone(zone.id)
                    })
                })
            }
        }
    })

    $effect(() => {
        const currentEditor = editor
        const coloring = lineColoring
        if (!currentEditor || !coloring?.ranges.length) return
        //The container survives model changes. Monaco replaces its inner view when Build switches
        //from live assembly to its snapshot, and also resets that view's classes on focus/theme.
        const root = currentEditor.getContainerDomNode()
        const scope = `compiled-colors-${currentEditor.getId().replace(/[^a-zA-Z0-9_-]/g, '-')}`
        root.dataset.sourceMapColorScope = scope
        const style = document.createElement('style')
        style.dataset.sourceMapColors = scope
        const used = new Set(coloring.ranges.map((range) => range.colorIndex))
        const activeColors = activeLineColors
        style.textContent = [...used]
            .map((index) => {
                const opacity =
                    activeColors === undefined
                        ? SOURCE_MAP_COLOR_OPACITY.default
                        : activeColors.has(index)
                          ? SOURCE_MAP_COLOR_OPACITY.active
                          : SOURCE_MAP_COLOR_OPACITY.dimmed
                return `[data-source-map-color-scope="${scope}"] .compiled-section-color-${index} { --compiled-section-color: ${coloring.colors[index]}; --compiled-section-opacity: ${opacity * 100}%; --compiled-section-border-color: color-mix(in srgb, ${coloring.colors[index]} ${opacity * SOURCE_MAP_SECTION_BORDER.opacity * 100}%, transparent); }`
            })
            .join('\n')
        document.head.appendChild(style)
        return () => {
            style.remove()
            delete root.dataset.sourceMapColorScope
        }
    })

    $effect(() => {
        const currentMonaco = monacoInstance
        const currentEditor = editor
        void sectionLayoutRevision
        if (activeModelKey && currentEditor && decorations && currentMonaco) {
            decorations.set([
                ...(lineColoring?.ranges ?? []).map((range) => ({
                    range: new currentMonaco.Range(range.startLine + 1, 1, range.endLine + 1, 1),
                    options: {
                        className: `compiled-section compiled-section-color-${range.colorIndex}`,
                        marginClassName: `compiled-section-margin compiled-section-color-${range.colorIndex}`,
                        isWholeLine: true,
                        zIndex: 0
                    }
                })),
                ...(SOURCE_MAP_SECTION_BORDER.width > 0
                    ? (lineColoring?.ranges ?? []).flatMap((range) => {
                          let first = range.startLine + 1
                          let last = range.endLine + 1
                          //Match the connector's endpoints when section lines are folded.
                          while (
                              first <= last &&
                              currentEditor.getLineHeightForPosition({
                                  lineNumber: first,
                                  column: 1
                              }) === 0
                          )
                              first++
                          while (
                              last >= first &&
                              currentEditor.getLineHeightForPosition({
                                  lineNumber: last,
                                  column: 1
                              }) === 0
                          )
                              last--
                          if (first > last) return []
                          const endpoints = [{ line: first, edge: 'top' }]
                          if (!viewZones.some((zone) => zone.afterLineNumber === last))
                              endpoints.push({ line: last, edge: 'bottom' })
                          return endpoints.map(({ line, edge }) => {
                              const className = `compiled-section-border-${edge} compiled-section-color-${range.colorIndex}`
                              return {
                                  range: new currentMonaco.Range(line, 1, line, 1),
                                  options: {
                                      className,
                                      marginClassName: className,
                                      isWholeLine: true,
                                      zIndex: 3
                                  }
                              }
                          })
                      })
                    : []),
                ...lineNoteDecorations(currentEditor, currentMonaco),
                ...patchedLines.map((line) => ({
                    range: new currentMonaco.Range(line + 1, 1, line + 1, 1),
                    options: {
                        isWholeLine: true,
                        className: 'patched-line',
                        linesDecorationsClassName: 'patched-line-marker',
                        zIndex: 1
                    }
                })),
                ...changedLines.map(({ line, runsNow }) => ({
                    range: new currentMonaco.Range(line + 1, 1, line + 1, 1),
                    options: {
                        isWholeLine: true,
                        className: 'patched-line',
                        linesDecorationsClassName: 'patched-line-marker',
                        linesDecorationsTooltip: `Code changed after Build. Current instruction: ${runsNow}`,
                        zIndex: 1
                    }
                })),
                ...breakpoints.map((e) => ({
                    range: new currentMonaco.Range(e + 1, 1, e + 1, 1),
                    options: {
                        glyphMarginClassName:
                            e === breakpointOnHighlightedLine
                                ? 'breakpoint-glyph selected-line-glyph'
                                : 'breakpoint-glyph'
                    }
                })),
                ...mappedBreakpoints.map((e) => ({
                    range: new currentMonaco.Range(e + 1, 1, e + 1, 1),
                    options: {
                        glyphMarginClassName:
                            e === breakpointOnHighlightedLine
                                ? 'mapped-breakpoint-glyph selected-line-glyph'
                                : 'mapped-breakpoint-glyph',
                        glyphMarginHoverMessage: {
                            value: 'Breakpoint set on the source line this instruction compiles from'
                        }
                    }
                })),
                ...(breakpointsEditable &&
                hoveredGliphen &&
                !breakpoints.includes(hoveredGliphen - 1) &&
                !mappedBreakpoints.includes(hoveredGliphen - 1)
                    ? [
                          {
                              range: new currentMonaco.Range(hoveredGliphen, 1, hoveredGliphen, 1),
                              options: {
                                  glyphMarginClassName: 'hovered-glyph'
                              }
                          }
                      ]
                    : [])
            ])
        }
    })

    $effect(() => {
        const currentMonaco = monacoInstance
        if (!activeModelKey || !lineDecorations || !currentMonaco) return
        const line = zeroBasedLineToMonaco(highlightedLine)
        lineDecorations.set(
            highlightedLine >= 0
                ? [
                      {
                          range: new currentMonaco.Range(line, 1, line, 1),
                          options: {
                              className: hasError ? 'error-line' : 'selected-line',
                              marginClassName: hasError ? 'error-line' : 'selected-line',
                              lineNumberClassName: hasError
                                  ? 'error-line-number'
                                  : 'selected-line-number',
                              inlineClassName: 'selected-line-text',
                              isWholeLine: true,
                              zIndex: 2
                          }
                      }
                  ]
                : []
        )
    })

    /**
     * The program's current line, revealed at once in a model just shown and smoothly after; not
     * while a Run is in flight, whose host reveals where it stops or waits for input. A line
     * scrolled to as it moved kept a running program's editor scrolling the whole time.
     */
    const currentLine = new CurrentLineReveal()
    $effect(() => {
        const scroll = monacoInstance?.editor.ScrollType
        if (running) return
        if (activeModelKey && editor && scroll && highlightedLine >= 0)
            currentLine.reveal(
                editor,
                scroll,
                activeModelKey,
                zeroBasedLineToMonaco(highlightedLine)
            )
    })
    $effect(() => {
        const currentMonaco = monacoInstance
        if (activeModelKey && editor && currentMonaco) {
            const model = editor.getModel()
            if (!model) return

            const markerSeverities = {
                error: currentMonaco.MarkerSeverity.Error,
                warning: currentMonaco.MarkerSeverity.Warning,
                suggestion: currentMonaco.MarkerSeverity.Info
            }
            //JSON, JavaScript, CSS and HTML report their own problems under their language's name,
            //which the editor's would replace
            const owner = isTextFileLanguage(language) ? `asm-editor-${language}` : language
            //another File's text, whichever view this model is
            const fileResource = (file: string) =>
                modelIdentity
                    ? projectSourceUri(currentMonaco, {
                          ...modelIdentity,
                          path: file,
                          view: undefined
                      })
                    : undefined
            const markers = diagnostics.map((e) => {
                const lineNumber = Math.min(Math.max(e.lineIndex + 1, 1), model.getLineCount())
                const maxColumn = model.getLineMaxColumn(lineNumber)
                let startColumn = Math.min(Math.max(e.column, 1), maxColumn)
                let endColumn = Math.min(
                    Math.max(e.endColumn ?? startColumn + 1, startColumn),
                    maxColumn
                )
                //An empty range draws nothing. A Core that reports a point, or one that points
                //at the end of the line — "expected an operand" — clamps to exactly that, so
                //widen it over the character beside it rather than leave an invisible marker.
                if (startColumn === endColumn) {
                    if (endColumn < maxColumn) endColumn += 1
                    else if (startColumn > 1) startColumn -= 1
                }
                return {
                    severity: markerSeverities[e.severity],
                    message: e.formatted,
                    source: e.source,
                    code: e.code,
                    startLineNumber: lineNumber,
                    startColumn,
                    endLineNumber: lineNumber,
                    endColumn,
                    relatedInformation: e.related?.map((related) => ({
                        resource: fileResource(related.file) ?? model.uri,
                        startLineNumber: related.lineIndex + 1,
                        startColumn: related.column,
                        endLineNumber: related.lineIndex + 1,
                        endColumn: related.endColumn,
                        message: related.message
                    }))
                }
            })
            currentMonaco.editor.setModelMarkers(model, owner, markers)
            //the fixes the Core suggests, kept beside the markers for the quick fix provider; one
            //into another File is offered where that File has a model to edit
            setModelQuickFixes(
                currentMonaco,
                model,
                owner,
                diagnostics.flatMap((e, index) => {
                    const fixes = (e.fixes ?? []).flatMap((fix) => {
                        const resource = fix.file === e.file ? model.uri : fileResource(fix.file)
                        return resource
                            ? [
                                  {
                                      title: fix.title,
                                      resource,
                                      range: quickFixRange(fix),
                                      text: fix.text
                                  }
                              ]
                            : []
                    })
                    return fixes.length > 0 ? [{ marker: markers[index], fixes }] : []
                })
            )
        }
    })

    $effect(() => {
        editor?.updateOptions({ readOnly: disabled })
    })
</script>

<div bind:this={mockEditor} class="mock-editor">
    {#if !editor}
        <h1 class="loading">Loading editor...</h1>
    {/if}
</div>

<div
    bind:this={el}
    class="editor"
    class:address-gutter={addressGutterShown}
    style:--compiled-section-border-width="{Math.max(0, SOURCE_MAP_SECTION_BORDER.width)}px"
></div>

<style lang="scss">
    :global(.compiled-section, .compiled-section-margin) {
        background-color: color-mix(
            in srgb,
            var(--compiled-section-color) var(--compiled-section-opacity),
            transparent
        );
    }
    :global(.compiled-section-border-top::before, .compiled-section-border-bottom::after) {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        height: var(--compiled-section-border-width);
        background-color: var(--compiled-section-border-color);
        pointer-events: none;
    }
    :global(.compiled-section-border-top::before) {
        top: 0;
        transform: translateY(-50%);
    }
    :global(.compiled-section-border-bottom::after) {
        bottom: 0;
        transform: translateY(50%);
    }
    :global(.selected-line) {
        background-color: var(--accent);
        color: var(--accent-text);
    }

    :global(.overflow-guard, .monaco-editor) {
        border-radius: 0.4rem;
    }

    :global(.error-line) {
        background-color: var(--red);
        color: var(--red-text);
    }

    :global(.selected-line-text) {
        color: var(--accent-text) !important;
    }

    /* the context menus' surface: a translucent tint of the widget's colour that blurs what is
       beneath, edged with their hairline (menu.border) and lifted by their shadow. The standalone
       editor defines no --vscode-widget-border or --vscode-shadow-lg, so Monaco's own were none */
    :global(.find-widget) {
        border: 1px solid var(--vscode-menu-border) !important;
        border-radius: 0.4rem !important;
        background-color: color-mix(
            in srgb,
            var(--vscode-editorWidget-background) 85%,
            transparent
        ) !important;
        backdrop-filter: blur(4px);
        box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.35) !important;
        top: 1rem !important;
        right: 2.4rem !important;
    }
    /* its resize edge drew a square grey strip across the rounded corner; it still lights up on
       hover, where it can be dragged */
    :global(.monaco-editor .find-widget .monaco-sash) {
        background-color: transparent !important;
    }
    :global(.monaco-editor .find-widget > .button.codicon-widget-close) {
        top: 7px !important;
    }

    :global(.editor-widget.suggest-widget) {
        border-radius: 0.3rem !important;
        overflow: hidden;
    }

    :global(.monaco-editor-overlaymessage .message) {
        border-radius: 0.3rem !important;
        border-bottom-left-radius: 0 !important;
    }

    :global(.monaco-inputbox) {
        border-radius: 0.2rem;
    }

    :global(.monaco-resizable-hover),
    :global(.monaco-hover) {
        border-radius: 0.4rem !important;
    }

    :global(.monaco-hover) {
        box-shadow: 0 3px 10px rgb(0 0 0 / 0.2);
        border: 1px solid var(--accent2) !important;
    }

    :global(.monaco-overflow-widgets) {
        position: fixed;
        z-index: 1000;
        inset: 0;
        background: transparent;
        pointer-events: none;
    }

    :global(.monaco-overflow-widgets .overflowingContentWidgets > *),
    :global(.monaco-overflow-widgets .overflowingOverlayWidgets > *) {
        pointer-events: auto;
    }

    :global(.monaco-editor .monaco-hover .hover-row:not(:first-child):not(:empty)) {
        border-top: 1px solid var(--accent2) !important;
    }

    :global(.monaco-editor .monaco-hover hr) {
        margin: 0.4rem 0 !important;
    }

    :global(.monaco-hover table) {
        border-collapse: collapse;
        overflow-x: auto;
        display: block;
        margin: 0.5rem 0;
        font-family: 'Fira Code', monospace;
        border-radius: 0.5rem;
        border: solid 0.1rem var(--primary);
        width: fit-content;
        max-width: 100%;
    }

    :global(.monaco-hover thead) {
        background-color: var(--tertiary);
        color: var(--tertiary-text);
    }

    :global(.monaco-hover thead th) {
        padding: 0.2rem 0.4rem;
        border-right: 0.1rem solid var(--secondary);
    }

    :global(.monaco-hover thead th:first-child) {
        border-top-left-radius: 0.3rem;
    }

    :global(.monaco-hover thead th:last-child) {
        border-top-right-radius: 0.3rem;
        border-right: unset;
    }

    :global(.monaco-hover tbody tr:nth-child(odd)) {
        background-color: color-mix(in srgb, var(--secondary), var(--tertiary) 20%);
    }

    :global(.monaco-hover tbody) {
        background-color: var(--secondary);
    }

    :global(.monaco-hover td) {
        padding: 0.2rem 0.4rem;
        border: 0.1rem solid var(--tertiary);
    }

    :global(.monaco-hover td:first-child) {
        border-left: unset;
    }

    :global(.monaco-hover td:last-child) {
        border-right: unset;
    }

    :global(.monaco-hover tr:last-child td) {
        border-bottom: unset;
    }

    /* far enough above the editor that its shadow does not show while it is closed */
    :global(.find-widget) {
        transform: translateY(calc(-100% - 3.5rem)) !important;
    }

    :global(.find-widget.visible) {
        transform: translateY(0) !important;
    }

    .mock-editor {
        display: flex;
        flex: 1;
    }

    //The glyph slot is one line high and wide, and line numbers are right-aligned just after it.
    //A circle pushed against the slot's right edge touches every number as wide as the column
    //(3 digits and up), so it sits near the left edge, and the numbers are nudged right to leave
    //a gap as wide as the circle's left margin that does not depend on the digits.
    .editor :global(.margin-view-overlays .line-numbers) {
        margin-left: 0.2rem;
    }

    //a Listing's gutter prints 64-bit addresses, 0.2rem smaller than the code
    //(ADDRESS_GUTTER_SHRINK_REM) so they take less of the width, centred on the line's height
    //rather than sitting on the code's baseline
    .editor.address-gutter :global(.margin-view-overlays .line-numbers) {
        font-size: calc(1em - 0.2rem);
        display: flex;
        align-items: center;
        justify-content: flex-end;
        height: 100%;
    }

    :global(.breakpoint-glyph),
    :global(.hovered-glyph) {
        width: calc(22px - 0.6rem) !important;
        height: calc(22px - 0.6rem) !important;
        margin-top: 0.3rem;
        cursor: pointer;
        margin-left: 0.4rem;
        background-color: var(--accent);
        border-radius: 1rem;
    }

    :global(.hovered-glyph) {
        background-color: var(--accent2) !important;
    }

    //a name a double-click follows, under the mouse
    :global(.followable-name) {
        text-decoration: underline;
        cursor: pointer;
    }

    //what runs when it is not what the line shows, after the line's text
    :global(.line-note) {
        color: var(--warning, #d49a30);
        font-style: italic;
    }

    //a line whose instruction a Debug session patched, or whose code the program changed: a bar
    //beside its address, and a tint
    :global(.patched-line-marker) {
        width: 3px !important;
        margin-left: 3px;
        background-color: var(--warning, #d49a30);
    }
    :global(.patched-line) {
        background-color: color-mix(in srgb, var(--warning, #d49a30) 12%, transparent);
    }

    :global(.selected-line-number) {
        color: var(--accent-text) !important;
    }

    :global(.error-line-number) {
        color: var(--red-text) !important;
    }

    //A breakpoint on the highlighted line would vanish into the same accent color.
    :global(.breakpoint-glyph.selected-line-glyph) {
        background-color: var(--accent-text);
    }

    :global(.mapped-breakpoint-glyph.selected-line-glyph) {
        border-color: var(--accent-text);
    }

    :global(.mapped-breakpoint-glyph) {
        width: calc(22px - 0.6rem) !important;
        height: calc(22px - 0.6rem) !important;
        margin-top: 0.3rem;
        margin-left: 0.4rem;
        cursor: pointer;
        box-sizing: border-box;
        border: 0.15rem solid var(--accent);
        border-radius: 1rem;
    }

    .editor {
        display: flex;
        position: absolute;
        inset: 0;
        flex: 1;
        z-index: 2;
        box-shadow: 0 3px 10px rgb(0 0 0 / 0.2);
    }

    .loading {
        display: flex;
        width: calc(100% - 0.4rem);
        height: calc(100% - 0.4rem);
        justify-content: center;
        align-items: center;
        background-color: var(--secondary);
        color: var(--secondary-text);
        border-radius: 0.4rem;
        animation: infinite 3s pulse ease-in-out;
        position: absolute;
    }

    @keyframes pulse {
        0% {
            background-color: var(--secondary);
        }
        50% {
            background-color: var(--primary);
        }
        100% {
            background-color: var(--secondary);
        }
    }
</style>
